import React, { useMemo } from 'react'
import CanvasLogo from '../../../assets/icons/canvas-logo.svg'
import {
  getEnrollmentDate,
  shouldInferLevelComplete,
  getLevelKeyFromCode,
  buildLevelTrackerData
} from './utils'
import { LEVEL, LEVEL_ORDER } from './utils/constants/levels'
import { LevelTrackerSection, RequirementsSection } from './ProgressTrackerContent'
import SuspendedProgressTracker from './SuspendedProgressTracker'
import {
  Container,
  Header,
  Title,
  AssociateHeader,
  Divider,
  ProgramInfoItem,
  ProgramInfoLabel,
  ProgramInfoLink,
  PortalButton,
  PortalButtonText
} from './Styles/ProgressTracker.styles'

const ProgressTracker = ({
  levelState,
  activeLevel = LEVEL.QUALIFYING,
  onLevelClick,
  programHistory = [],
  prospect,
  currentLevelCode,
  isSuspended = false
}) => {
  const { patRequirement = null, shouldShowPATWarning = false } = levelState || {}

  const levels = useMemo(() => {
    const baseLevels = buildLevelTrackerData(levelState, activeLevel, programHistory, currentLevelCode)
    
    return baseLevels.map(level => ({
      ...level,
      hasIncompleteRequirements: level.key === LEVEL.QUALIFYING && shouldShowPATWarning
    }))
  }, [levelState, activeLevel, programHistory, currentLevelCode, shouldShowPATWarning])

  const currentProgramLevel = useMemo(() => 
    programHistory?.find(p => getLevelKeyFromCode(p.levelCode) === activeLevel),
    [programHistory, activeLevel]
  )

  const shouldShowLevelCompleteBanner = useMemo(() =>
    shouldInferLevelComplete(activeLevel, programHistory, currentLevelCode, levelState, prospect),
    [activeLevel, programHistory, currentLevelCode, levelState, prospect]
  )

  const currentLevel = levelState[activeLevel]
  const enrollmentDate = getEnrollmentDate(programHistory)
  const levelCompletionDate = currentProgramLevel?.endDate || null
  const patRequirementWithWarning = activeLevel === LEVEL.QUALIFYING && shouldShowPATWarning ? patRequirement : null

  const userCurrentLevelKey = getLevelKeyFromCode(currentLevelCode)
  const userCurrentLevelIndex = LEVEL_ORDER.indexOf(userCurrentLevelKey)
  const activeLevelIndex = LEVEL_ORDER.indexOf(activeLevel)
  const shouldShowAdditionalRequirements = activeLevelIndex >= userCurrentLevelIndex

  const additionalRequirements = activeLevel !== LEVEL.QUALIFYING && patRequirement && shouldShowAdditionalRequirements ? [patRequirement] : []

  if (isSuspended) {
    return (
      <SuspendedProgressTracker
        levels={levels}
        enrollmentDate={enrollmentDate}
        currentLevel={currentLevel}
        levelCompletionDate={levelCompletionDate}
        patRequirementWithWarning={patRequirementWithWarning}
        additionalRequirements={additionalRequirements}
        activeLevel={activeLevel}
        shouldShowPATWarning={shouldShowPATWarning}
        shouldShowLevelCompleteBanner={shouldShowLevelCompleteBanner}
        shouldShowAdditionalRequirements={shouldShowAdditionalRequirements}
        onLevelClick={onLevelClick}
      />
    )
  }

  return (
    <Container>
      <Header>
        <Title>My Path to Membership</Title>
        {!!enrollmentDate && (
          <AssociateHeader>
            <ProgramInfoItem>
              <ProgramInfoLabel>Associate Program</ProgramInfoLabel>
              <ProgramInfoLink href="https://www.pga.org/membership/associate-program/" target="_blank" rel="noopener noreferrer">Details ›</ProgramInfoLink>
            </ProgramInfoItem>
            <Divider />
            <ProgramInfoItem>
              <ProgramInfoLabel>Enrolled: {enrollmentDate}</ProgramInfoLabel>
              <ProgramInfoLink href="https://www.pga.org/membership/Former-Associate/pga-associate-acceptable-progress-tracking" target="_blank" rel="noopener noreferrer">Guidelines ›</ProgramInfoLink>
            </ProgramInfoItem>
          </AssociateHeader>
        )}
      </Header>

      <LevelTrackerSection 
        levels={levels}
        onLevelClick={onLevelClick}
        isSuspended={false}
      />

      <RequirementsSection
        shouldShowLevelCompleteBanner={shouldShowLevelCompleteBanner}
        currentLevel={currentLevel}
        levelCompletionDate={levelCompletionDate}
        patRequirementWithWarning={patRequirementWithWarning}
        additionalRequirements={additionalRequirements}
        activeLevel={activeLevel}
        shouldShowPATWarning={shouldShowPATWarning}
        shouldShowAdditionalRequirements={shouldShowAdditionalRequirements}
        onLevelClick={onLevelClick}
      />

      <PortalButton href="https://pgaofamerica.instructure.com/" target="_blank" rel="noopener noreferrer">
        <img src={CanvasLogo} alt="Canvas Logo" />
        <PortalButtonText>Education Portal</PortalButtonText>
      </PortalButton>
    </Container>
  )
}

export default ProgressTracker
